'use client'
import * as React from 'react'
import { useTranslations } from 'next-intl'
import { usePublicUserProfileQuery, useUserQuery } from 'codegen/generated/user'
import { SkeletonItem } from 'components/skeleton-item'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { decodeHtmlEntities } from 'utils/decode-html-entities'
import Image from 'next/image'
import { useCheckFollowingQuery } from '@web/gen/user'
import { formatUserName } from '../../../masterfeed/utils/format-user-name'
import { VerifiedPremiumRibbon } from '@web/app/[locale]/clubhouse/[slug]/_components/verified-premium-ribbon'
import type { PublicUserProfile } from 'codegen/types'
import { FollowButton } from '@web/app/[locale]/clubhouse/[slug]/members/_components/follow-button'
import {
  IconAssignmentInd,
  IconEdit,
  IconSchool,
  IconStorefront,
} from '@repo/design-system-cf/components/icons'
import { Button } from '@repo/design-system-cf/components/button'
import { useUserCoinBalanceQuery } from 'codegen/generated/coins'
import { UserNameLabel } from '../../../masterfeed/_components/user-name-label'
import { CoinIcon } from '../../../masterfeed/_components/icons'
import { useUserIsDesigner, useUserIsTeacher } from '@repo/cf-user/hooks/use-user-data'
import { CopyUserIdButton } from '../../../masterfeed/_components/copy-user-id-button'

// oxlint-disable-next-line complexity
export function ProfileHeader({ slug }: { slug: string }) {
  const t = useTranslations('social-feed.components.feed.post')
  const userIsDesigner = useUserIsDesigner()
  const userIsTeacher = useUserIsTeacher()
  const { data: publicUserProfileQuery, isLoading: isPublicLoading } = usePublicUserProfileQuery({
    slug: slug,
  })

  const userId = publicUserProfileQuery?.publicUserProfile.id || ''

  const { data: user, isLoading: isUserLoading } = useUserQuery()
  const isOwn =
    Boolean(user?.me?.user?.id) &&
    (!slug ||
      (publicUserProfileQuery?.publicUserProfile?.slug &&
        publicUserProfileQuery?.publicUserProfile.slug === user?.me?.user?.slug))

  const coinsQuery = useUserCoinBalanceQuery(undefined, { enabled: !!isOwn })
  const formattedBalance = React.useMemo(
    () => coinsQuery.data?.userBalance.balance.toLocaleString('en-US'),
    [coinsQuery.data?.userBalance.balance],
  )

  // Follow state
  const { data: following } = useCheckFollowingQuery(
    { input: { targetUserId: publicUserProfileQuery?.publicUserProfile?.id } },
    { enabled: !isOwn && Boolean(publicUserProfileQuery?.publicUserProfile?.id) },
  )

  const userName = formatUserName(
    isOwn ? user?.me?.user?.firstName : publicUserProfileQuery?.publicUserProfile?.firstName,
    isOwn ? user?.me?.user?.lastName : publicUserProfileQuery?.publicUserProfile?.lastName,
    t('user_label'),
    decodeHtmlEntities,
  )

  const avatarUrl = publicUserProfileQuery?.publicUserProfile?.avatarUrl || ''
  const superSkill = publicUserProfileQuery?.publicUserProfile?.superSkill
  const isLoading = isPublicLoading || isUserLoading

  if (isLoading) {
    return <ProfileHeaderSkeleton />
  }

  return (
    <div className="sm:px-7 sm:py-6 rounded-[12px] border border-[#1313151F] mt-6 mb-4 lg:mt-0 lg:mb-0 p-4 sm:pb-5 bg-cf-white">
      <div className="sm:flex-row gap-4 sm:gap-6 lg:gap-0 flex flex-col sm:items-start">
        <div className="flex justify-between">
          <div className="lg:hidden relative w-[89px] h-[112px] sm:w-[108px] sm:h-[135px] shrink-0 -mt-11 pt-0">
            <Image
              src={avatarUrl}
              alt="Avatar"
              fill
              sizes="(min-width: 640px) 108px, 89px"
              className="object-cover rounded-4 -rotate-6 shadow-[0px_0.91px_2.73px_0px_#0000004D,0px_3.64px_7.27px_2.73px_#00000026]"
            />
          </div>
          {isOwn ? (
            <div className="sm:hidden">
              <Button variant="tertiary">
                <Link
                  href={AppRoutes.MY_ACCOUNT_PROFILE}
                  target="_blank"
                  className="w-full flex items-center justify-center gap-1.5"
                >
                  <IconEdit className="!size-5" />
                  {t('edit_profile')}
                </Link>
              </Button>
            </div>
          ) : (
            <div className="sm:hidden">
              <FollowButton
                following={following?.usersCheckFollowing}
                userId={publicUserProfileQuery?.publicUserProfile?.id as string}
                className="w-full"
                isShowUnfollowWithoutIcon
              />
            </div>
          )}
        </div>
        <div className="flex w-full flex-col sm:items-start">
          <div className="sm:justify-between flex sm:w-full pb-3 sm:pb-0">
            <div className="sm:flex gap-2">
              <UserNameLabel className="font-bold text-20 text-cf-grey-1000 text-center">
                {userName}
                <VerifiedPremiumRibbon
                  user={publicUserProfileQuery?.publicUserProfile as unknown as PublicUserProfile}
                />
              </UserNameLabel>

              <CopyUserIdButton userId={userId} />
            </div>
            {isOwn ? (
              <div className="hidden sm:block lg:hidden">
                <Button variant="tertiary">
                  <Link
                    href={AppRoutes.MY_ACCOUNT_PROFILE}
                    target="_blank"
                    className="w-full flex items-center justify-center gap-1.5"
                  >
                    <IconEdit className="!size-5" />
                    {t('edit_profile')}
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="hidden sm:block lg:hidden">
                <FollowButton
                  following={following?.usersCheckFollowing}
                  userId={publicUserProfileQuery?.publicUserProfile?.id as string}
                  className="w-full"
                  isShowUnfollowWithoutIcon
                />
              </div>
            )}
          </div>
          {superSkill ? (
            <div className="lg:hidden inline-flex w-fit border border-[#1313151F] bg-cf-pink-100 px-2 py-0.5 rounded-[24px] justify-center items-center gap-0.5">
              <IconAssignmentInd className="size-3.5 text-cf-pink-800" />
              <span className="text-cf-pink-800 font-semibold text-center text-xs">
                {superSkill}
              </span>
            </div>
          ) : null}
          {/* Own-only: Coins */}
          {isOwn && formattedBalance ? (
            <Link
              href="/coins"
              target="_blank"
              className="flex flex-row items-center gap-0.5 lg:hidden py-4"
            >
              <CoinIcon />
              <span className="ml-1 text-14 font-bold text-cf-grey-1000">{formattedBalance}</span>
            </Link>
          ) : null}

          {/* Own-only buttons (Designer/Teacher) */}
          {isOwn && (userIsDesigner || userIsTeacher) ? (
            <div className="flex gap-2 lg:hidden">
              {userIsDesigner &&
              publicUserProfileQuery?.publicUserProfile?.profileUrls?.designer ? (
                <Button variant="secondary">
                  <Link
                    href={publicUserProfileQuery?.publicUserProfile?.profileUrls?.designer || ''}
                    target="_blank"
                    className="flex items-center justify-center gap-1.5"
                  >
                    <IconStorefront className="size-4" />
                    {t('shop_text')}
                  </Link>
                </Button>
              ) : null}
              {userIsTeacher && publicUserProfileQuery?.publicUserProfile?.profileUrls?.teacher ? (
                <Button variant="secondary">
                  <Link
                    href={publicUserProfileQuery?.publicUserProfile?.profileUrls?.teacher}
                    target="_blank"
                    className="flex items-center justify-center gap-1.5"
                  >
                    <IconSchool className="size-4" />
                    {t('classes_text')}
                  </Link>
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export function ProfileHeaderSkeleton() {
  return (
    <div className="sm:px-7 sm:py-6 rounded-[12px] border border-[#1313151F] mt-6 mb-4 lg:mt-0 lg:mb-0 p-4 sm:pb-5 bg-cf-white">
      <div className="sm:flex-row gap-4 sm:gap-6 lg:gap-0 flex flex-col sm:items-start">
        {/* Left: avatar + mobile button */}
        <div className="flex justify-between">
          {/* Mobile avatar */}
          <div className="lg:hidden relative w-[89px] h-[112px] sm:w-[108px] sm:h-[135px] shrink-0 -mt-11 pt-0">
            <SkeletonItem className="w-full h-full rounded-4 -rotate-6 shadow-[0px_0.91px_2.73px_0px_#0000004D,0px_3.64px_7.27px_2.73px_#00000026]" />
          </div>

          {/* Mobile primary action button (Edit / Follow) */}
          <div className="sm:hidden">
            <SkeletonItem className="h-[40px] w-[140px] rounded-[8px]" />
          </div>
        </div>

        {/* Right: main content */}
        <div className="flex w-full flex-col sm:items-start">
          {/* Username row + desktop/tablet primary action */}
          <div className="sm:justify-between flex sm:w-full pb-3 sm:pb-0">
            <div className="flex items-center gap-2">
              {/* Username + verified */}
              <SkeletonItem className="h-[24px] w-[180px] rounded-[999px]" />

              {/* CF internal info (ID + copy) */}
              <div className="hidden sm:flex items-center pl-2 gap-1">
                <SkeletonItem className="h-[16px] w-[90px] rounded-[999px]" />
                <SkeletonItem className="h-[24px] w-[24px] rounded-full" />
              </div>
            </div>

            {/* Tablet primary action (Edit / Follow) */}
            <div className="hidden sm:block lg:hidden">
              <SkeletonItem className="h-[40px] w-[140px] rounded-[8px]" />
            </div>
          </div>

          {/* Super skill pill */}
          <div className="lg:hidden mt-1">
            <SkeletonItem className="inline-flex h-[24px] w-[160px] rounded-[24px]" />
          </div>

          {/* Coins */}
          <div className="flex flex-row items-center gap-0.5 lg:hidden pt-4">
            <SkeletonItem className="h-[20px] w-[20px] rounded-full" />
            <SkeletonItem className="ml-1 h-[18px] w-[80px] rounded-[999px]" />
          </div>

          {/* Followers / Following */}
          <div className="w-full py-4 flex gap-4">
            <SkeletonItem className="h-[18px] w-[120px] rounded-[999px]" />
            <SkeletonItem className="h-[18px] w-[120px] rounded-[999px]" />
          </div>

          {/* Own-only buttons (Designer / Teacher) */}
          <div className="flex gap-2 lg:hidden">
            <SkeletonItem className="h-[38px] w-[130px] rounded-[8px]" />
            <SkeletonItem className="h-[38px] w-[130px] rounded-[8px]" />
          </div>
        </div>
      </div>
    </div>
  )
}
